import { useGetAccountsQuery } from "@/modules/account/services";
import type {
  IAccountClosureApplicationForm,
  IAccountClosureApplicationRequest
} from "@/modules/application/common/types";
import {
  useCreateAccountClosureApplicationMutation,
  useGetAccountClosureApplicationQuery,
  useUpdateAccountClosureApplicationMutation
} from "@/modules/application/services";
import { INITIAL_PAGE_SIZE, INPUT_MAX_LIMIT, } from "@/shared/common/constants";
import type { IExtendedObject, IOperationPageProps } from "@/shared/common/types";
import { OwnAccountSelectOption, Select } from "@/shared/components";
import { ApplicationLayout } from "@/shared/components/ApplicationLayout";
import { renderSelectedAccount } from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { Alert } from "@ab.uitools/ui-library/components/Alert";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";

import { ApplicationTermsAndConditionsLink } from "../ApplicationTermsAndConditionsLink";

export const AccountClosureApplicationForm = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, watch, reset } = useFormProps();
  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    data: accountsList = [],
    isLoading: isGetAccountsLoading,
  } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: accountClosureApplication,
    isLoading: isGetApplicationLoading,
  } = useGetAccountClosureApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const [
    createAccountClosureApplicationApi,
    { isLoading: isCreateLoading }
  ] = useCreateAccountClosureApplicationMutation();

  const [
    updateAccountClosureApplicationApi,
    { isLoading: isUpdateLoading }
  ] = useUpdateAccountClosureApplicationMutation();

  const accountClosureApplicationAction = useCallback((values: IAccountClosureApplicationForm) => {
    const requestData: IAccountClosureApplicationRequest = {
      ...values,
      closureAccount: accountsList?.find(({ number }) => number === values.closureAccount),
    };
    return isEdit ? updateAccountClosureApplicationApi({
        data: requestData,
        id: `${accountClosureApplication?.id}`,
      }).unwrap() :
      createAccountClosureApplicationApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, accountClosureApplication]);

  const accountsOptions = useMemo(() =>
    accountsList.map((item, i) => ({
      value: item.number,
      label: renderSelectedAccount(item),
      meta: { ...item, index: i },
    })), [accountsList]);

  const values = watch?.() || {};

  const setDefaultValues = () => {
    const data = {
      applicationNumber: isEdit ?
        accountClosureApplication?.applicationNumber : values.applicationNumber,
      purpose: accountClosureApplication?.purpose,
      closureAccount: accountClosureApplication?.closureAccount?.number,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    applicationNumber: values.applicationNumber,
    purpose: values.purpose,
    closureAccount: values.closureAccount,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!accountClosureApplication?.id) {
      setDefaultValues();
    }
  }, [accountClosureApplication]);

  return (
    <ApplicationLayout
      title={t("application.accountClosure.title")}
      page="Account closure application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={accountClosureApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <Alert
                type="warning"
                position="inline"
                text={t("application.accountClosure.warning")}
                className="mb-20"
              />
              <FormField
                name="closureAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    className="custom-select"
                    options={accountsOptions}
                    isDisabled={isGetAccountsLoading}
                    label={t("application.accountClosure.account")}
                    placeholder={t("application.accountClosure.chooseClosureAccount")}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-12"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <FormField
                name="isAgreeWithConditions"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    isInvalid={!!errors.isAgreeWithConditions}
                    label={<ApplicationTermsAndConditionsLink/>}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
